/* eslint-disable */
// File taken and modified from SBN:https://github.com/voxmedia/sbn/blob/master/app_unison/frontend/scripts/lib/analytics.js

import AnalyticsClient from "@framework/analytics/analytics-client";
declare global {
  interface Window {
    dataLayer?: Record<string, string | number>[];
  }
}

export default class VideoAnalytics extends AnalyticsClient {
  constructor() {
    super(false);
  }
  /**
   * Pushes a new message onto the analytics dataLayer.
   */
  push(message: any) {
    super.emit(message);
  }

  /**
   * Analytics Event object formatting helper.
   * This helper transforms raw arguments into a normalized Event object schema.
   * The formatted event object is returned WITHOUT being reported.
   * @function
   * @param {...Any} args used to build an event object.
   * Positional arguments will automatically be mapped to field names.
   * @returns {Object} a formatted event object for use with GTM.
   */
  formatEvent(args: any[]) {
    return analyticsFormatter(
      args,
      "event",
      "analyticsEvent",
      [
        "eventCategory",
        "eventAction",
        "eventLabel",
        "eventValue",
        "eventNonInt",
      ],
      {
        nonInteraction: "eventNonInt",
        hitCallback: "eventCallback",
      }
    );
  }
  /**
   * Analytics Event reporter.
   * Transforms raw arguments into a normalized message schema,
   * and then pushes the event message onto the data layer.
   * Accepts analytics arguments to be formatted and sent to GTM.
   * @param {...Any} parameters used to build a pageview object.
   */
  event(...parameters: any[]) {
    this.push(this.formatEvent(parameters));
  }
}

/**
 * Creates a formatter function to build positional arguments into event objects.
 * This is a brute-force normalization of anything that you can throw at analytics.
 * @param {String} eventName name assigned to the formatted analytics data.
 * @param {String[]} fields names arranged by call position.
 * @param {Object} uaMapping hash defining fields to rename.
 * @return {Function} a GTM formatter function that accepts analytics arguments,
 * and returns a formatted analytics event object.
 * @private
 */
function analyticsFormatter(
  args: any[],
  uaAction: string,
  eventName: string,
  fields: string[],
  uaMapping: { [x: string]: any }
) {
  // Sanitize arguments array, removing all GA boilerplate from initial arguments:
  // this will take out "send", "event", etc, from the front of the array...
  if (args[0] instanceof Array) {
    args = args[0];
  }
  if (typeof args[0] === "string" && args[0].match(/send|_track/)) {
    args.shift();
  }
  if (args[0] === uaAction.toLowerCase()) {
    args.shift();
  }

  let position = 0;
  const evt: {
    [x: string]: any | undefined;
  } = {};

  // Map passed positional arguments into named event keys:
  // this loop runs while we have arguments OR named fields left to fill.
  for (let i = 0; i < args.length || position < fields.length; i++) {
    const value = args[i];

    // Formatting rules:
    // - Any function param is assigned as the event callback.
    // - Any boolean param is assigned as the non-interaction flag.
    // - Any object params are extended onto the event object.
    // --> (a mapping table may specify the renaming of legacy field names).
    // - Otherwise, params are positionally mapped into named fields.
    if (typeof value === "function") {
      // Set any passed function as the event callback:
      evt.eventCallback = value;
    } else if (typeof value === "boolean") {
      // Set any boolean as a non-interaction flag:
      if (value) {
        evt.eventNonInt = value;
      }
    } else if (typeof value === "object") {
      // Extend any passed objects onto the event:
      // Object keys are subject to field renaming.
      // Ex: "hitCallback" -> "eventCallback"
      for (const key in value) {
        if (value.hasOwnProperty(key)) {
          evt[uaMapping[key] || key] = value[key];
        }
      }
    } else if (fields[position]) {
      // Assign everything else to a positional label:
      const field = fields[position++];
      if (field && !evt.hasOwnProperty(field)) {
        evt[field] = value;
      }
    }
  }

  // Set the definitive event name:
  evt.event = eventName;
  return evt;
}
